import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { setCanonical, setPageTitle, setMetaDescription, generateArticleSchema } from "@/utils/seo";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import { SidebarAds, BottomAdBanner } from "@/components/SponsorBanners";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold text-slate-900 mt-6 mb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-slate-900 mt-8 mb-3">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-slate-900 mt-8 mb-3">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g, '<a href="$2" class="text-emerald-600 hover:underline" target="_blank" rel="noopener">$1</a>')
    .replace(/^- (.*$)/gm, '<li class="ml-4 text-slate-600">$1</li>')
    .replace(/\n\n/g, '</p><p class="text-slate-600 leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>');
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/blogs/${slug}`).then(r => {
      setBlog(r.data);
      setCanonical(`/blog/${slug}`);
      setPageTitle(r.data.title);
      setMetaDescription(r.data.meta_description || r.data.title);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-main py-24 text-center text-slate-400">Loading...</div>;
  if (!blog) return (
    <div className="container-main py-24 text-center">
      <h1 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h1>
      <Link to="/blog"><Button variant="outline">Back to Blog</Button></Link>
    </div>
  );

  return (
    <div data-testid="blog-post-page">
      <script type="application/ld+json">{JSON.stringify(generateArticleSchema(blog.title, slug, blog.created_at, blog.meta_description || blog.title))}</script>

      <div className="bg-slate-50 border-b border-slate-100 py-3">
        <div className="container-main flex items-center gap-2 text-xs text-slate-500">
          <Link to="/" className="hover:text-slate-700 no-underline">Home</Link><span>/</span>
          <Link to="/blog" className="hover:text-slate-700 no-underline">Blog</Link><span>/</span>
          <span className="text-slate-700 truncate max-w-[200px]">{blog.title}</span>
        </div>
      </div>

      <div className="container-main py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
          <article>
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 no-underline mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <h1 className="speakable-headline text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">{blog.title}</h1>
            <p className="text-sm text-slate-400 mb-8">
              {blog.created_at ? new Date(blog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
            </p>
            <div className="prose prose-slate max-w-none"
              dangerouslySetInnerHTML={{ __html: `<p class="text-slate-600 leading-relaxed mb-4">${renderMarkdown(blog.content)}</p>` }} />
            
            <FinancialDisclaimer />

            <div className="mt-8 pt-6 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500 mb-3">Want to see the numbers for your city?</p>
              <Link to="/"><Button className="bg-emerald-700 hover:bg-emerald-800">Try the Salary Calculator</Button></Link>
            </div>
          </article>

          {/* Sidebar Ads — Blog uses apartment instead of CreditCane */}
          <div className="hidden lg:block">
            <div className="sticky-sidebar space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5 text-center">
                <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
                <div className="bg-slate-50 rounded-lg py-20 px-6 border border-dashed border-slate-200">
                  <p className="text-xs text-slate-400">Google AdSense</p>
                  <p className="text-[10px] text-slate-300">300x600</p>
                </div>
              </div>
              <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
                <div className="p-5 bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:shadow-md transition-all">
                  <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=260&h=140&fit=crop" alt="Furnished apartment" className="w-full h-24 rounded-lg object-cover mb-3" />
                  <p className="text-sm font-bold text-slate-900 mb-1">Get $250 Off Your First Apartment</p>
                  <p className="text-xs text-slate-500 mb-3">Move freely, stay comfortably. *Limited time offer.</p>
                  <span className="inline-block text-xs font-semibold text-white bg-blue-600 px-3 py-1.5 rounded-md">Claim $250 Off</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      <BottomAdBanner />
    </div>
  );
}
